import useSWR, { mutate } from 'swr';

import { RouteSearchUsageEvent } from '@/types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { getSearchHistoryUrl } from '@/utils/helpers/api-urls';

export interface SearchHistoryApiResponse {
  events: RouteSearchUsageEvent[];
}

interface SearchHistoryState extends AsyncDataInterface, SearchHistoryApiResponse {}

interface UseSearchHistoryProps {
  perPageLimit?: number;
  page?: number;
  hasAccess?: boolean;
}

const SEARCH_HISTORY_KEY = getSearchHistoryUrl();
const ERROR_MESSAGE = 'Error fetching search history data';

if (typeof window !== 'undefined') {
  const data = localStorage.getItem(SEARCH_HISTORY_KEY);
  if (data) mutate(SEARCH_HISTORY_KEY, JSON.parse(data), false);
}

export const useSearchHistory = ({ perPageLimit, page, hasAccess }: UseSearchHistoryProps): SearchHistoryState => {
  const searchUrl = getSearchHistoryUrl(perPageLimit, page);

  const { data, isLoading, error } = useSWR<SearchHistoryApiResponse>(hasAccess ? searchUrl : null, request, {
    errorRetryCount: 1,
    revalidateOnFocus: false,
    keepPreviousData: true,
    onSuccess(events) {
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(events));
    },
  });

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  return { events: data?.events || [], isLoading, error: errorMessage };
};
